import { DolsStatus } from '@lmc/ts-common'
import {
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Select
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { FormInstance, RadioChangeEvent } from 'antd'
import type { RuleObject } from 'antd/lib/form'
import { FormWarning } from 'components/forms/FormWarning'
import type dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { capitalize } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { SupDatePicker } from '../../../styledComponents'
import { dateFormat, dateFormatTime } from '../../../utils/utils'

const dolsStatuses = Object.values(DolsStatus)
const dolsStatusesOptions = [
  ...dolsStatuses.map((status) => ({
    label: capitalize(status),
    value: status
  })),
  { label: 'No Status', value: null }
]

type LpsFormCardProps = {
  dols: boolean
  isDoLSOverride: boolean
  form: FormInstance
}

export const LpsFormCard = ({
  dols,
  isDoLSOverride,
  form
}: LpsFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [showDateFields, setShowDateFields] = useState(dols)
  const [showEndDateWarning, setShowEndDateWarning] = useState(false)
  const dolsModsAlignment = useFeatureFlags({
    flagNames: ['dols_mods_alignment']
  }).data?.dols_mods_alignment.enabled

  const checkIfEndDateWarningNeeded = () => {
    const dolsEndDate = form.getFieldValue('dolsEndDate')
    setShowEndDateWarning(!!dolsEndDate)
  }

  const onRadioChange = (e: RadioChangeEvent) => {
    setShowDateFields(e.target.value === true)
  }

  const endDateValidator = async (
    _rule: RuleObject,
    endDate: dayjs.Dayjs
  ): Promise<void> => {
    const startDate = form.getFieldValue('dolsStartDate')
    if (!endDate || !startDate) {
      return
    }
    if (endDate.isBefore(startDate)) {
      throw new Error(t(supConstants.LPS_END_DATE_VALIDATION))
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // init dols / dolsStatus field based on the feature flag
    // should be removed with the feature flag deprecation
    if (dolsModsAlignment) {
      const dolsStatus: DolsStatus = form.getFieldValue('dolsStatus')
      form.setFieldValue('dols', dolsStatus === DolsStatus.authorised)
      form.setFieldValue('dolsStatus', dolsStatus || null)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    checkIfEndDateWarningNeeded()
  }, [])

  const getOptionalDateFields = () => {
    return (
      <>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_APPLICATION_DATE)
                : t(supConstants.LPS_APPLICATION_DATE)}
            </Text>
          }
          name="dolsApplicationDate"
        >
          <SupDatePicker placeholder="" format={dateFormatTime} showTime />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_START_DATE)
                : t(supConstants.LPS_START_DATE)}
            </Text>
          }
          name="dolsStartDate"
        >
          <SupDatePicker placeholder="" format={dateFormatTime} showTime />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_END_DATE)
                : t(supConstants.LPS_END_DATE)}
            </Text>
          }
        >
          <FormItem
            name="dolsEndDate"
            rules={[
              {
                validator: endDateValidator,
                message: isDoLSOverride
                  ? t(supConstants.DOLS_END_DATE_VALIDATION)
                  : t(supConstants.LPS_END_DATE_VALIDATION)
              }
            ]}
          >
            <SupDatePicker
              placeholder=""
              format={dateFormatTime}
              onChange={() => checkIfEndDateWarningNeeded()}
              showTime
            />
          </FormItem>
          {showEndDateWarning && (
            <FormWarningWrapper>
              <FormWarning
                message={
                  isDoLSOverride
                    ? t(supConstants.DOLS_END_DATE_WARNING)
                    : t(supConstants.LPS_END_DATE_WARNING)
                }
              />
            </FormWarningWrapper>
          )}
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_REVIEW_DATE)
                : t(supConstants.LPS_REVIEW_DATE)}
            </Text>
          }
          name="dolsReviewDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
      </>
    )
  }

  const getRequiredDateFileds = () => {
    return (
      <>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_APPLICATION_DATE)
                : t(supConstants.LPS_APPLICATION_DATE)}
            </Text>
          }
          name="dolsApplicationDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_START_DATE)
                : t(supConstants.LPS_START_DATE)}
            </Text>
          }
          name="dolsStartDate"
          rules={[
            {
              required: true,
              message: `${t(supConstants.PLEASE_ENTER)} ${
                isDoLSOverride
                  ? t(supConstants.DOLS_START_DATE)
                  : t(supConstants.LPS_START_DATE)
              }`
            }
          ]}
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_END_DATE)
                : t(supConstants.LPS_END_DATE)}
            </Text>
          }
        >
          <FormItem
            name="dolsEndDate"
            rules={[
              {
                required: true,
                message: `${t(supConstants.PLEASE_ENTER)} ${
                  isDoLSOverride
                    ? t(supConstants.DOLS_END_DATE)
                    : t(supConstants.LPS_END_DATE)
                }`
              },
              {
                validator: endDateValidator,
                message: isDoLSOverride
                  ? t(supConstants.DOLS_END_DATE_VALIDATION)
                  : t(supConstants.LPS_END_DATE_VALIDATION)
              }
            ]}
          >
            <SupDatePicker
              placeholder=""
              format={dateFormat}
              onChange={() => checkIfEndDateWarningNeeded()}
            />
          </FormItem>
          {showEndDateWarning && (
            <FormWarningWrapper>
              <FormWarning
                message={
                  isDoLSOverride
                    ? t(supConstants.DOLS_END_DATE_WARNING)
                    : t(supConstants.LPS_END_DATE_WARNING)
                }
              />
            </FormWarningWrapper>
          )}
        </FormItem>
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DOLS_REVIEW_DATE)
                : t(supConstants.LPS_REVIEW_DATE)}
            </Text>
          }
          name="dolsReviewDate"
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
      </>
    )
  }

  return (
    <FormCard
      title={isDoLSOverride ? t(supConstants.DOLS) : t(supConstants.LPS)}
      helpArticleUrl="https://support.logmycare.co.uk/en/articles/6534074-managing-lps-dols-on-client-profiles"
    >
      {dolsModsAlignment && (
        <FormItem
          label={
            <Text>
              {isDoLSOverride
                ? t(supConstants.DEPRIVATION)
                : t(supConstants.LIBERTY_PROTECTION_SAFEGUARD)}
            </Text>
          }
          colon={false}
          name="dolsStatus"
        >
          <Select
            defaultValue={null}
            optionRender={(option) => <Text>{option.label}</Text>}
            options={dolsStatusesOptions}
            onChange={(value: DolsStatus) => {
              form.setFieldValue('dols', value === DolsStatus.authorised)
            }}
          />
        </FormItem>
      )}

      <FormItem
        noStyle={dolsModsAlignment}
        label={
          <Text>
            {isDoLSOverride
              ? t(supConstants.DEPRIVATION)
              : t(supConstants.LIBERTY_PROTECTION_SAFEGUARD)}
          </Text>
        }
        name="dols"
        colon={false}
      >
        {!dolsModsAlignment && (
          <FormRadioGroup onChange={onRadioChange}>
            <FormRadio value={false} text={t(supConstants.NO)} />
            <FormRadio value text={t(supConstants.YES)} />
          </FormRadioGroup>
        )}
      </FormItem>

      {dolsModsAlignment
        ? getOptionalDateFields()
        : showDateFields && getRequiredDateFileds()}
    </FormCard>
  )
}

const FormWarningWrapper = styled.div`
  margin-top: 20px;
`
